import React, { useState } from 'react';
import useSWR from 'swr';
import { Link } from 'react-router-dom';
import { ShieldAlert, Scissors } from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useAuth } from '@/context/AuthContext';
import { formatEuro, formatDate } from '@/lib/format';
import * as A from '@/lib/approvals/service';
import { SchemaNotice, Loading, StatusPill, ReadinessGate } from '@/components/common/F1States';
import { useSchemaReady } from '@/lib/schemaReady';
import { AuthoritySettings } from '@/components/approvals/AuthoritySettings';

function RequestTable({ rows, testId }) {
  if (!rows?.length) return <div className="p-8 text-center text-sm text-muted-foreground" data-testid={`${testId}-empty`}>No approval requests in this view. Requests are raised from purchasing documents (CC-2); none exist yet.</div>;
  return (
    <Table data-testid={testId}>
      <TableHeader><TableRow><TableHead>Type</TableHead><TableHead>Title</TableHead><TableHead>Requested</TableHead><TableHead className="text-right">Net</TableHead><TableHead className="text-right">VAT</TableHead><TableHead className="text-right">Gross (incl. VAT)</TableHead><TableHead>Status</TableHead><TableHead>Flags</TableHead></TableRow></TableHeader>
      <TableBody>
        {rows.map((r) => (
          <TableRow key={r.id} data-testid={`approval-row-${r.id}`}>
            <TableCell className="text-sm">{A.typeLabel(r.transaction_type)}</TableCell>
            <TableCell><Link to={`/approvals/${r.id}`} className="font-medium hover:underline">{r.title || r.entity_id.slice(0, 8)}</Link></TableCell>
            <TableCell className="text-sm text-muted-foreground">{formatDate(r.requested_at)}</TableCell>
            <TableCell className="text-right tabular-nums text-sm">{formatEuro(r.amount_net)}</TableCell>
            <TableCell className="text-right tabular-nums text-sm">{formatEuro(r.amount_vat)}</TableCell>
            <TableCell className="text-right tabular-nums font-medium">{formatEuro(r.amount_gross)}</TableCell>
            <TableCell><StatusPill value={r.status} /></TableCell>
            <TableCell className="text-xs">
              {r.split_suspected && <span className="inline-flex items-center gap-1 text-amber-800 mr-2" title="Possible split purchase"><Scissors size={12} /> Split</span>}
              {r.attention_required && <span className="inline-flex items-center gap-1 text-red-800" title="No approver available"><ShieldAlert size={12} /> Attention</span>}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

export default function ApprovalsPage() {
  const { organisationId, role } = useAuth();
  const isOwner = role === 'owner';
  const manager = ['owner', 'admin', 'manager'].includes(role || '');
  const [tab, setTab] = useState('mine');
  const readiness = useSchemaReady(organisationId && 'approvals', ['approval_requests', 'approval_authorities', 'v_my_approvals']);
  const ready = readiness.state === 'ready';
  const mine = useSWR(ready ? ['my-approvals'] : null, A.listMyApprovals);
  const all = useSWR(organisationId && ready && tab !== 'mine' && tab !== 'authority' ? ['approvals', organisationId, tab] : null, () => A.listRequests(organisationId, { status: tab === 'pending' ? 'open' : tab }));
  const err = mine.error || all.error;

  if (!ready) {
    return (
      <div data-testid="approvals-page" className="p-6 md:p-8 max-w-7xl mx-auto">
        <PageHeader title="Approvals" subtitle="Delegated approvals — VAT-inclusive limits, Owner escalation, 14-day split-purchase detection" />
        <ReadinessGate readiness={readiness} module="The Approvals Inbox" migration="f1_foundation_migration.sql" testId="approvals-migration-pending" />
      </div>
    );
  }

  return (
    <div data-testid="approvals-page" className="p-6 md:p-8 max-w-7xl mx-auto">
      <PageHeader title="Approvals" subtitle="Delegated approvals — VAT-inclusive limits, Owner escalation, 14-day split-purchase detection" />
      {err && <div className="mb-4"><SchemaNotice error={err} testId="approvals-schema-notice" /></div>}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="mine" data-testid="approvals-tab-mine">My approvals{mine.data?.length ? ` (${mine.data.length})` : ''}</TabsTrigger>
          <TabsTrigger value="pending" data-testid="approvals-tab-pending">Pending</TabsTrigger>
          <TabsTrigger value="approved" data-testid="approvals-tab-approved">Approved</TabsTrigger>
          <TabsTrigger value="rejected" data-testid="approvals-tab-rejected">Rejected</TabsTrigger>
          {manager && <TabsTrigger value="authority" data-testid="approvals-tab-authority">Delegated authority</TabsTrigger>}
        </TabsList>
        <div className="rounded-lg border border-border bg-white overflow-x-auto mt-3">
          <TabsContent value="mine" className="m-0">{mine.isLoading ? <Loading /> : <RequestTable rows={mine.data} testId="approvals-mine" />}</TabsContent>
          {['pending', 'approved', 'rejected'].map((t) => <TabsContent key={t} value={t} className="m-0">{all.isLoading ? <Loading /> : <RequestTable rows={all.data} testId={`approvals-${t}`} />}</TabsContent>)}
          {manager && <TabsContent value="authority" className="m-0 p-4"><AuthoritySettings organisationId={organisationId} isOwner={isOwner} /></TabsContent>}
        </div>
      </Tabs>
    </div>
  );
}
